import type { InputHTMLAttributes } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';

import {
  type ParsedPlaceAddress,
  type PlaceDetailsResult,
  parsePlaceResult,
} from '../utils/googlePlaces';
import styles from './GoogleAddressInput.module.scss';

type GoogleAddressInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'ref'> & {
  autocompleteEnabled: boolean;
  onAddressSelect?: (address: ParsedPlaceAddress) => void;
};

type AutocompletePrediction = {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
};

type AutocompleteSessionToken = unknown;

type PlacesServiceStatus =
  | 'OK'
  | 'ZERO_RESULTS'
  | 'INVALID_REQUEST'
  | 'OVER_QUERY_LIMIT'
  | 'UNKNOWN_ERROR'
  | string;

type AutocompleteService = {
  getPlacePredictions(
    request: {
      input: string;
      sessionToken: AutocompleteSessionToken;
      types?: string[];
    },
    callback: (
      results: AutocompletePrediction[] | null,
      status: PlacesServiceStatus,
    ) => void,
  ): void;
};

type PlacesService = {
  getDetails(
    request: {
      placeId: string;
      fields: string[];
      sessionToken: AutocompleteSessionToken;
    },
    callback: (place: PlaceDetailsResult | null, status: PlacesServiceStatus) => void,
  ): void;
};

type GooglePlacesNamespace = {
  AutocompleteService?: new () => AutocompleteService;
  PlacesService?: new (node: Element) => PlacesService;
  AutocompleteSessionToken?: new () => AutocompleteSessionToken;
};

type GoogleMapsNamespace = {
  places?: GooglePlacesNamespace;
};

type GoogleLike = {
  maps?: GoogleMapsNamespace;
};

type WindowWithGoogle = typeof window & { google?: GoogleLike };

const AUTOCOMPLETE_DEBOUNCE_MS = 275;

const isDevEnvironment = () => import.meta.env.DEV;

const logDev = (...args: unknown[]) => {
  if (isDevEnvironment()) {
    console.debug(...args);
  }
};

const GoogleAddressInput = forwardRef<HTMLInputElement, GoogleAddressInputProps>(
  (
    {
      autocompleteEnabled,
      onAddressSelect,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      className,
      ...props
    },
    forwardedRef,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const autocompleteServiceRef = useRef<AutocompleteService | null>(null);
    const placesServiceRef = useRef<PlacesService | null>(null);
    const placesServiceNodeRef = useRef<HTMLDivElement | null>(null);
    const sessionTokenRef = useRef<AutocompleteSessionToken | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const metricsRef = useRef({ autoRequests: 0, detailsCalls: 0 });
    const shouldAttemptSessionRef = useRef(true);
    const activeRequestIdRef = useRef(0);
    const debounceHandleRef = useRef<number | null>(null);
    const blurTimeoutRef = useRef<number | null>(null);
    const lastQueryRef = useRef<string>('');
    const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
    const [isFocused, setIsFocused] = useState(false);
    const listboxId = useId();

    const inputValue = useMemo(() => {
      if (typeof props.value === 'string') {
        return props.value;
      }

      return '';
    }, [props.value]);

    const ensureAutocompleteService = useCallback(() => {
      if (typeof window === 'undefined') {
        return null;
      }

      const googleInstance = (window as WindowWithGoogle).google;
      const places = googleInstance?.maps?.places;
      if (!places?.AutocompleteService) {
        return null;
      }

      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current =
          new places.AutocompleteService() as unknown as AutocompleteService;
      }

      return autocompleteServiceRef.current;
    }, []);

    const ensurePlacesService = useCallback(() => {
      if (typeof window === 'undefined') {
        return null;
      }

      const googleInstance = (window as WindowWithGoogle).google;
      const places = googleInstance?.maps?.places;
      if (!places?.PlacesService) {
        return null;
      }

      if (!placesServiceRef.current) {
        if (!placesServiceNodeRef.current) {
          placesServiceNodeRef.current = document.createElement('div');
        }

        placesServiceRef.current =
          new places.PlacesService(
            placesServiceNodeRef.current,
          ) as unknown as PlacesService;
      }

      return placesServiceRef.current;
    }, []);

    const resetSessionState = useCallback(() => {
      sessionTokenRef.current = null;
      sessionIdRef.current = null;
      metricsRef.current = { autoRequests: 0, detailsCalls: 0 };
      lastQueryRef.current = '';
      activeRequestIdRef.current += 1;
    }, []);

    const startSession = useCallback(() => {
      if (!shouldAttemptSessionRef.current) {
        return;
      }

      if (!autocompleteEnabled) {
        return;
      }

      const googleInstance = (window as WindowWithGoogle).google;
      const places = googleInstance?.maps?.places;
      if (!places?.AutocompleteSessionToken) {
        return;
      }

      const token = new places.AutocompleteSessionToken();
      sessionTokenRef.current = token;
      const sessionId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionIdRef.current = sessionId;
      metricsRef.current = { autoRequests: 0, detailsCalls: 0 };
      lastQueryRef.current = '';
      logDev('[Places] Session started', { sessionId });
    }, [autocompleteEnabled]);

    const finishSession = useCallback(
      (detailsCalls: number) => {
        const sessionId = sessionIdRef.current;
        if (sessionId) {
          logDev('[Places] Session summary', {
            sessionId,
            autoRequests: metricsRef.current.autoRequests,
            detailsCalls,
          });
        }

        resetSessionState();
        shouldAttemptSessionRef.current = false;
      },
      [resetSessionState],
    );

    const handlePredictionSelection = useCallback(
      (prediction: AutocompletePrediction) => {
        const sessionToken = sessionTokenRef.current;
        const sessionId = sessionIdRef.current;
        const placesService = ensurePlacesService();

        if (!sessionToken || !placesService) {
          return;
        }

        metricsRef.current.detailsCalls += 1;
        if (sessionId) {
          logDev('[Places] Place details request', {
            sessionId,
            placeId: prediction.place_id,
          });
        }

        setPredictions([]);

        placesService.getDetails(
          {
            placeId: prediction.place_id,
            fields: [
              'place_id',
              'address_components',
              'formatted_address',
              'geometry.location',
            ],
            sessionToken,
          },
          (
            placeResult: PlaceDetailsResult | null,
            status: PlacesServiceStatus,
          ) => {
            if (status === 'OK' && placeResult) {
              const parsed = parsePlaceResult(placeResult);
              if (Object.keys(parsed).length > 0) {
                onAddressSelect?.(parsed);
              }
            }

            finishSession(metricsRef.current.detailsCalls);
          },
        );
      },
      [ensurePlacesService, finishSession, onAddressSelect],
    );

    const sendAutocompleteRequest = useCallback(
      (query: string) => {
        const service = ensureAutocompleteService();
        const sessionToken = sessionTokenRef.current;
        const sessionId = sessionIdRef.current;

        if (!service || !sessionToken) {
          return;
        }

        activeRequestIdRef.current += 1;
        const requestId = activeRequestIdRef.current;

        metricsRef.current.autoRequests += 1;
        if (sessionId) {
          logDev('[Places] Autocomplete request', {
            sessionId,
            autoRequests: metricsRef.current.autoRequests,
          });
        }

        lastQueryRef.current = query.trim();

        service.getPlacePredictions(
          {
            input: query,
            sessionToken,
            types: ['address'],
          },
          (
            results: AutocompletePrediction[] | null,
            status: PlacesServiceStatus,
          ) => {
            if (requestId !== activeRequestIdRef.current) {
              return;
            }

            if (status !== 'OK' || !results || results.length === 0) {
              setPredictions([]);
              return;
            }

            setPredictions(results);
          },
        );
      },
      [ensureAutocompleteService],
    );

    const clearDebounce = useCallback(() => {
      if (debounceHandleRef.current) {
        window.clearTimeout(debounceHandleRef.current);
        debounceHandleRef.current = null;
      }
    }, []);

    useEffect(() => {
      if (!autocompleteEnabled) {
        setPredictions([]);
        clearDebounce();
        resetSessionState();
        return;
      }

      ensureAutocompleteService();
      ensurePlacesService();
    }, [
      autocompleteEnabled,
      clearDebounce,
      ensureAutocompleteService,
      ensurePlacesService,
      resetSessionState,
    ]);

    useEffect(() => {
      if (
        !isFocused ||
        !autocompleteEnabled ||
        !shouldAttemptSessionRef.current
      ) {
        return;
      }

      if (!sessionTokenRef.current) {
        startSession();
      }
    }, [autocompleteEnabled, isFocused, startSession]);

    useEffect(() => {
      if (!isFocused || !autocompleteEnabled) {
        clearDebounce();
        setPredictions([]);
        return;
      }

      const trimmed = inputValue.trim();

      if (trimmed.length < 3) {
        clearDebounce();
        setPredictions([]);
        if (trimmed.length === 0) {
          lastQueryRef.current = '';
        }
        return;
      }

      if (trimmed === lastQueryRef.current) {
        return;
      }

      clearDebounce();

      debounceHandleRef.current = window.setTimeout(() => {
        sendAutocompleteRequest(inputValue);
      }, AUTOCOMPLETE_DEBOUNCE_MS);

      return () => {
        clearDebounce();
      };
    }, [
      autocompleteEnabled,
      clearDebounce,
      inputValue,
      isFocused,
      sendAutocompleteRequest,
    ]);

    useEffect(
      () => () => {
        clearDebounce();
        if (blurTimeoutRef.current) {
          window.clearTimeout(blurTimeoutRef.current);
        }
      },
      [clearDebounce],
    );

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        if (blurTimeoutRef.current) {
          window.clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }

        shouldAttemptSessionRef.current = true;
        setIsFocused(true);
        startSession();
        onFocus?.(event);
      },
      [onFocus, startSession],
    );

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        onBlur?.(event);

        blurTimeoutRef.current = window.setTimeout(() => {
          setIsFocused(false);
          setPredictions([]);
          resetSessionState();
          shouldAttemptSessionRef.current = true;
        }, 100);
      },
      [onBlur, resetSessionState],
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
        if (!sessionTokenRef.current && autocompleteEnabled) {
          shouldAttemptSessionRef.current = true;
          startSession();
        }
      },
      [autocompleteEnabled, onChange, startSession],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          if (predictions.length > 0) {
            event.preventDefault();
            handlePredictionSelection(predictions[0]);
            return;
          }
        } else if (event.key === 'Escape') {
          setPredictions([]);
        }

        onKeyDown?.(event);
      },
      [handlePredictionSelection, onKeyDown, predictions],
    );

    const handleUseTypedSelection = useCallback(() => {
      if (!inputValue) {
        return;
      }

      onAddressSelect?.({ line1: inputValue, line2: '', city: '', state: '', postalCode: '' });
      finishSession(0);
      setPredictions([]);
    }, [finishSession, inputValue, onAddressSelect]);

    const showUseAsTypedOption = inputValue.trim().length > 0;
    const hasSessionToken = sessionTokenRef.current !== null;
    const shouldShowSuggestions =
      autocompleteEnabled &&
      isFocused &&
      (predictions.length > 0 || (showUseAsTypedOption && hasSessionToken));

    const inputClassName = [styles.input, className].filter(Boolean).join(' ');

    return (
      <div className={styles.wrapper}>
        <input
          {...props}
          className={inputClassName}
          ref={(node) => {
            inputRef.current = node;
            if (typeof forwardedRef === 'function') {
              forwardedRef(node);
            } else if (forwardedRef) {
              forwardedRef.current = node;
            }
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          role={autocompleteEnabled ? 'combobox' : undefined}
          aria-autocomplete={autocompleteEnabled ? 'list' : undefined}
          aria-expanded={
            autocompleteEnabled ? shouldShowSuggestions : undefined
          }
          aria-controls={
            shouldShowSuggestions ? listboxId : undefined
          }
        />
        {shouldShowSuggestions && (
          <ul className={styles.suggestions} role="listbox" id={listboxId}>
            {predictions.map((prediction) => (
              <li key={prediction.place_id} role="option">
                <button
                  type="button"
                  className={styles.suggestionButton}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handlePredictionSelection(prediction)}
                >
                  <span className={styles.primaryText}>
                    {prediction.structured_formatting?.main_text ??
                      prediction.description}
                  </span>
                  <span className={styles.secondaryText}>
                    {prediction.structured_formatting?.secondary_text ?? ''}
                  </span>
                </button>
              </li>
            ))}
            {showUseAsTypedOption && (
              <li key="use-as-typed" role="option">
                <button
                  type="button"
                  className={styles.useAsTypedButton}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleUseTypedSelection}
                >
                  Use "{inputValue}" as typed
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    );
  },
);

GoogleAddressInput.displayName = 'GoogleAddressInput';

export default GoogleAddressInput;
